<script setup lang="ts">
import AppMap from '@/components/map/AppMap.vue'
import AppHeading from '@/components/app/AppHeading.vue'
import { County, Municipality, RegionPages, RegionType } from '~/types/types'
import { usePopulationDataFetch } from '~/composables/statisticsData'
import { useMapStore } from '~/stores/map'
import { LayerPresets } from '~/types/map'
import { PopulationData } from '~/types/statistics'
import AppLoading from '@/components/app/AppLoading.vue'
import { useGeoDataStore } from '~/stores/geoData'
import MapSplitView from '@/components/layout/MapSplitView.vue'

const props = defineProps<{
  regionType: RegionType
  regionId: string
  regionObject: County | Municipality
  regionContent: RegionPages
}>()

const { formattedNumber } = useFormattedNumber()
const { data: populationData } = usePopulationDataFetch<PopulationData>(
  props.regionType,
  props.regionId
)

const mapStore = useMapStore()
const loadMap = ref(false)

const geoDataStore = useGeoDataStore()
const county = computed(() => geoDataStore.getCountyByCountyId(props.regionObject.county_code))

const { updateFiltersFromQueryParams } = useGlobalFilters()

onMounted(() => {
  setTimeout(() => {
    loadMap.value = true
  }, 500)
  geoDataStore.initCounties()
  updateFiltersFromQueryParams()
  props.regionType === 'county' && mapStore.setActiveCounty(props.regionId)
  props.regionType === 'municipality' && mapStore.setActiveMunicipality(props.regionId)
})

onUnmounted(() => {
  props.regionType === 'county' && mapStore.clearActiveCounty()
  props.regionType === 'municipality' && mapStore.clearActiveMunicipality()
})

const layerPresets: LayerPresets = {
  municipalities: { visible: props.regionType === 'municipality' },
  counties: { visible: props.regionType === 'county' },
  stations: {
    visible: true,
    dataFilters: [
      'station.fuelTypes',
      'station.stationType',
      'stationCategories',
    ],
  },
  'electricity-grid': { visible: false },
  'infrastructure-opportunities': { visible: true },
}

function translateRegionType(type: string) {
  if (type === 'municipality') return 'kommuner'
  if (type === 'county') return 'län'
}
</script>
<template>
  <div class="main">
    <MapSplitView>
      <template #content>
        <AppLink
          class="link-back"
          :title="`Utforska fler ${translateRegionType(regionType)}`"
          path="/utforska-en-plats"
          icon="ArrowLeftIcon"
          icon-position="left"
        />
        <div class="mt-1 mb-3">
          <span v-if="props.regionType === 'county' || props.regionType === 'municipality'">
            <NuxtLink :to="`/utforska-en-plats/sverige`">Sverige</NuxtLink>
          </span>
          <span v-if="props.regionType === 'county' || props.regionType === 'municipality'">
            /
            <span v-if="props.regionType === 'county'"> {{ county?.name }}</span>
            <NuxtLink v-else :to="`/utforska-en-plats/region/${county?.slug}`">{{
              county?.name
            }}</NuxtLink>
          </span>
          <span v-if="props.regionType === 'municipality'">
            /
            {{ regionObject.name }}
          </span>
        </div>
        <AppHeading type="h1" :title="regionObject.name" />
        <p class="mt-2 info">
          Befolkningsmängd: {{ formattedNumber(populationData?.population) }} ({{
            populationData?.year
          }})
          <DataSource :source-data="[{ identifier: 'scb' }]" />
        </p>
        <!-- eslint-disable vue/no-v-html -->
        <p v-if="regionContent.content" v-html="regionContent.content.replace(/\n/g, '<br />')"></p>
        <!--eslint-enable-->
        <RegionStatisticsBundle :region-type="regionType" :region-id="regionId" />
      </template>
      <template #map>
        <AppMap
          v-if="loadMap"
          :focus-to-region="{ regionType, regionId }"
          :layer-presets="layerPresets"
        />
        <div v-else class="loader-box"><AppLoading /></div>
      </template>
    </MapSplitView>
  </div>
</template>
<style lang="scss" scoped>
@import 'App.scss';
.main {
  height: calc(100% - $topbarHeight);
}
.loader-box {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
}
.data {
  padding: 24px;
  height: 100%;
  overflow-y: scroll;
  background-color: #fff;
}
.map {
  height: 100%;
  width: 100%;
  padding: 0;
}
.info {
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: space-between;
  font-size: 0.875rem;
}
</style>
